import { formatMoney } from '@/lib/format'
import { cmp, div, money, mul, type Money } from '@/lib/money'
import { moneySchema } from '@/schemas/common'
import {
  metricKeySchema,
  type Metric,
  type MetricRange,
  type MetricRanges,
  type ScreenFilter,
  type ScreenQuery,
  type ScreenSort,
} from '@/schemas/screener'

/**
 * The screen's filters as one search param, `f=pe:5~15,market_cap:10000000000~`: each metric's
 * key, then its inclusive bounds in mm-be's own unit around a `~`, either side left empty. One
 * plain string rather than a `min_<key>` param per metric: a key set only `/metrics/` knows cannot
 * be a route's typed search, and a record-typed param widens every route's search type.
 */

const ENTRY_SEPARATOR = ','
const KEY_SEPARATOR = ':'
const BOUND_SEPARATOR = '~'

const parseBound = (text: string | undefined): Money | null => {
  if (text === undefined || text.trim() === '') return null
  const parsed = moneySchema.safeParse(text.trim())
  return parsed.success ? parsed.data : null
}

/** A pair, or null when it is no filter: both bounds empty, or a minimum over its maximum. */
export const toRange = (min: Money | null, max: Money | null): MetricRange | null => {
  if (min === null) return max === null ? null : { min: null, max }
  if (max !== null && cmp(min, max) > 0) return null
  return { min, max }
}

/**
 * `f` as ranges. An entry that will not read (no key, no bound, a minimum over its maximum) is
 * dropped, not the rest: a hand-edited link still lands on a screen.
 */
export const parseFilters = (text: string | undefined): MetricRanges => {
  if (text === undefined) return {}
  const ranges: Record<string, MetricRange> = {}
  for (const entry of text.split(ENTRY_SEPARATOR)) {
    const [key, bounds] = entry.split(KEY_SEPARATOR)
    if (key === undefined || bounds === undefined || !metricKeySchema.safeParse(key).success) {
      continue
    }
    const [min, max] = bounds.split(BOUND_SEPARATOR)
    const range = toRange(parseBound(min), parseBound(max))
    if (range !== null) ranges[key] = range
  }
  return ranges
}

/** Ranges as `f`, or undefined for none, so an unfiltered screen carries no param. */
export const serializeFilters = (ranges: MetricRanges): string | undefined => {
  const entries = Object.entries(ranges).map(
    ([key, { min, max }]) => `${key}${KEY_SEPARATOR}${min ?? ''}${BOUND_SEPARATOR}${max ?? ''}`,
  )
  return entries.length === 0 ? undefined : entries.join(ENTRY_SEPARATOR)
}

/**
 * Only the ranges on metrics `/metrics/` serves: mm-be answers an unknown `min_`/`max_` key with a
 * 400, so a stale link's retired metric is dropped here rather than failing the page.
 */
export const knownRanges = (ranges: MetricRanges, keys: ReadonlySet<string>): MetricRanges =>
  Object.fromEntries(Object.entries(ranges).filter(([key]) => keys.has(key)))

/** `sort=` as a key and a direction. */
export const parseSort = (
  text: string | undefined,
  keys: ReadonlySet<string>,
): ScreenSort | null => {
  if (text === undefined) return null
  const desc = text.startsWith('-')
  const key = desc ? text.slice(1) : text
  return keys.has(key) ? { key, desc } : null
}

export const serializeSort = (sort: ScreenSort | null): string | undefined =>
  sort === null ? undefined : `${sort.desc ? '-' : ''}${sort.key}`

/** The keys `sort=` takes besides the metrics. */
export const SORT_ONLY_KEYS = ['symbol', 'name'] as const

/** The request's query: the ranges as `min_<key>` / `max_<key>`, then the sector, order and page. */
export const filterParams = ({ ranges, sector }: ScreenFilter): Record<string, string> => {
  const params: Record<string, string> = {}
  for (const [key, { min, max }] of Object.entries(ranges)) {
    if (min !== null) params[`min_${key}`] = min
    if (max !== null) params[`max_${key}`] = max
  }
  if (sector !== null) params['sector'] = sector
  return params
}

export const queryParams = (query: ScreenQuery): Record<string, string | number> => ({
  ...filterParams(query),
  ...(query.sort === null ? {} : { sort: serializeSort(query.sort) ?? '' }),
  page: query.page,
  page_size: query.pageSize,
})

/**
 * Company totals in rupees, read and written in crore (the mock's "Rs 3,420 Cr"): a market cap
 * typed in rupees is eleven digits nobody counts. Price is the other rupee metric and stays per
 * share. A rupee metric mm-be adds later is treated as a price until it is named here.
 */
const CRORE_METRICS: ReadonlySet<string> = new Set(['market_cap', 'ev'])

const CRORE = 10_000_000

export const isCroreMetric = (key: string): boolean => CRORE_METRICS.has(key)

/** A bound as the member types and reads it: crore for a company total, else as served. */
export const toInputValue = (metric: Metric, value: Money): string =>
  isCroreMetric(metric.key) ? div(value, CRORE) : value

/** What the member typed, in mm-be's unit. */
export const fromInputValue = (metric: Metric, value: Money): Money =>
  isCroreMetric(metric.key) ? mul(value, CRORE) : value

/** The prefix and suffix a metric's figure wears in a filter field. */
export const unitAffixes = (metric: Metric): { prefix?: string; suffix?: string } => {
  if (isCroreMetric(metric.key)) return { prefix: 'Rs', suffix: 'Cr' }
  switch (metric.unit) {
    case 'npr':
      return { prefix: 'Rs' }
    case 'percent':
      return { suffix: '%' }
    case 'times':
      return { suffix: '×' }
    case 'shares':
    case 'number':
      return {}
  }
}

const boundText = (metric: Metric, value: Money): string => {
  const shown = formatMoney(money(toInputValue(metric, value)))
    .replace(/\.00$/, '')
    .replace(/(\.\d)0$/, '$1')
  return isCroreMetric(metric.key) ? `Rs ${shown} Cr` : shown
}

/** An applied tag's value: `5 – 15`, `≥ 5`, `≤ 15`. */
export const rangeText = (metric: Metric, { min, max }: MetricRange): string => {
  if (min !== null && max !== null) return `${boundText(metric, min)} – ${boundText(metric, max)}`
  if (min !== null) return `≥ ${boundText(metric, min)}`
  return `≤ ${boundText(metric, max)}`
}
